import {Skeleton, Stack} from '@mui/material';
import {FC, useMemo} from 'react';
import {styled} from 'styled-components';

import {HousePreviewCardView} from '@website_frontend/components/house_preview_card_view';
import {useDbData} from '@website_frontend/lib/db_cache';

interface HousePreviewCardProps {
  id: number;
}

export const HousePreviewCard: FC<HousePreviewCardProps> = props => {
  const {id} = props;
  const {data, state} = useDbData();
  const item = useMemo(() => data?.get(id), [data, id]);

  // Show skeleton when data is still loading (data is undefined or state indicates loading)
  const isLoading = data === undefined || (state.type !== 'success' && state.type !== 'error');

  if (isLoading || !item) {
    return <HousePreviewCardSkeleton />;
  }

  // // If data loaded but item doesn't exist, return null (item truly doesn't exist)
  // if (!item) {
  //   return null;
  // }

  return <HousePreviewCardView item={item} />;
};

HousePreviewCard.displayName = 'HousePreviewCard';

const HousePreviewCardSkeleton: FC = () => {
  return (
    <SkeletonWrapper>
      <SkeletonImageContainer>
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '6px 6px 0 0',
          }}
        />
      </SkeletonImageContainer>
      <Stack padding={2} gap={1}>
        {/* Price and type label */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Skeleton variant="text" width="60%" height={32} animation="wave" />
          <Skeleton
            variant="rectangular"
            width={80}
            height={24}
            animation="wave"
            sx={{borderRadius: 1}}
          />
        </Stack>

        <Stack gap={0.5}>
          {/* Title */}
          <Skeleton variant="text" width="80%" height={24} animation="wave" />
          {/* Description */}
          <Skeleton variant="text" width="100%" height={16} animation="wave" />
          <Skeleton variant="text" width="95%" height={16} animation="wave" />
          <Skeleton variant="text" width="90%" height={16} animation="wave" />
        </Stack>

        {/* Meta chips */}
        <Stack direction="row" spacing={0.75} useFlexGap sx={{minHeight: '24px'}}>
          <Skeleton
            variant="rectangular"
            width={60}
            height={24}
            animation="wave"
            sx={{borderRadius: 1}}
          />
          <Skeleton
            variant="rectangular"
            width={50}
            height={24}
            animation="wave"
            sx={{borderRadius: 1}}
          />
          <Skeleton
            variant="rectangular"
            width={40}
            height={24}
            animation="wave"
            sx={{borderRadius: 1}}
          />
        </Stack>
      </Stack>

      <BottomSkeletonStack
        direction="row"
        px={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Skeleton variant="text" width="40%" height={20} animation="wave" />
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Skeleton variant="circular" width={24} height={24} animation="wave" />
          <Skeleton variant="circular" width={24} height={24} animation="wave" />
          <Skeleton variant="circular" width={24} height={24} animation="wave" />
        </Stack>
      </BottomSkeletonStack>
    </SkeletonWrapper>
  );
};

const SkeletonWrapper = styled(Stack)`
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  background-color: #fff;
  overflow: hidden;
  border-radius: 6px;
  @media (max-width: 512px) {
    border-radius: 0;
  }
`;

const BottomSkeletonStack = styled(Stack)`
  background-color: #f5f5f5;
  height: 41.5px;
`;

const SkeletonImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 75%; /* 4/3 aspect ratio, matching ImageCarousel */
  overflow: hidden;
`;
